/**
 * Slim event bridge: maps AgentDriver events to rendered console lines.
 * No React, no ink, no UI state — just event → string → callback.
 */

import type { OutputLine, SessionState } from "@skaile/workspaces";
import type { AgentEvent } from "@skaile/workspaces/bridge";
import type { AgentSession } from "@skaile/workspaces/runner";
import { renderLine, renderLog } from "./repl-renderer.js";
import type { TelemetryLogEntry } from "./telemetry.js";

/**
 * Handle returned by {@link createReplSession} for controlling an active REPL session.
 *
 * `state` reflects the current lifecycle state (`idle`, `active`, or `stopped`).
 * Call `send(text)` to forward a user message to the agent driver; the state
 * transitions to `active` until the driver emits `agent_end`. Call `stop()` to
 * terminate the driver and dispose of the underlying agent session.
 *
 * @docLink packages/tui/concepts#repl-session
 */
export interface ReplSessionHandle {
  readonly state: SessionState;
  send(text: string): void;
  stop(): void;
}

/**
 * Options for {@link createReplSession}.
 *
 * @param session - The agent session returned by `createAgentSession`.
 * @param onLine - Callback invoked with an ANSI-rendered string for each output line.
 * @param onLog - Callback invoked with a log string for each telemetry event (if verbose).
 * @param onStateChange - Callback invoked whenever the session state changes.
 *
 * @docLink packages/tui/concepts#repl-session
 */
export interface CreateReplSessionOpts {
  session: AgentSession;
  onLine?: (line: string) => void;
  onLog?: (line: string) => void;
  onStateChange?: (state: SessionState) => void;
}

/**
 * Create a slim event bridge that maps `AgentEvent` stream to rendered console lines.
 *
 * Subscribes to `agent-event` on the session driver and converts each event type
 * (`message_update`, `tool_call`, `error`, `agent_end`, `turn_end`, `subagent`) to
 * an `OutputLine`, renders it via {@link renderLine}, and forwards the string to
 * `opts.onLine`. Text content is buffered internally and flushed line-by-line as
 * newlines arrive. Returns a {@link ReplSessionHandle} for sending messages and
 * stopping the session.
 *
 * @param opts - Session, output callbacks, and state-change hook.
 * @returns Handle for sending messages and observing session state.
 *
 * @docLink packages/tui/concepts#repl-session
 */
export function createReplSession(opts: CreateReplSessionOpts): ReplSessionHandle {
  const { session, onLine, onLog, onStateChange } = opts;

  let state: SessionState = "idle";
  let textBuffer = "";

  function emit(outputLine: OutputLine) {
    const rendered = renderLine(outputLine);
    if (rendered) onLine?.(rendered);
  }

  function setState(s: SessionState) {
    state = s;
    onStateChange?.(s);
  }

  // Buffer the incoming text delta and flush every completed (newline-terminated)
  // line; returns the new buffer holding the trailing partial line.
  function handleMessageUpdate(event: AgentEvent, buffer: string): string {
    const delta = (event as any)._textDelta as string | undefined;
    if (!delta) return buffer;
    let next = buffer + delta;
    const lines = next.split("\n");
    if (lines.length > 1) {
      for (let i = 0; i < lines.length - 1; i++) {
        emit({ type: "text", content: lines[i]! });
      }
      next = lines[lines.length - 1]!;
    }
    return next;
  }

  function handleToolCall(event: AgentEvent) {
    const name = (event as any).tool?.name ?? (event as any).name ?? "unknown";
    emit({ type: "tool", name });
  }

  function handleError(event: AgentEvent) {
    const msg = (event as any).error ?? "Unknown error";
    emit({ type: "error", message: String(msg) });
    if ((event as any).fatal) setState("stopped");
  }

  // Flush any buffered text, emit a separator, and (on agent_end) go idle;
  // returns the cleared buffer.
  function handleTurnBoundary(event: AgentEvent, buffer: string): string {
    if (buffer.trim()) {
      emit({ type: "text", content: buffer });
    }
    emit({ type: "separator" });
    if (event.type === "agent_end") setState("idle");
    return "";
  }

  function handleSubagent(event: AgentEvent) {
    const raw = event as any;
    if (raw.type !== "subagent") return;
    const status = raw.status === "finished" ? "finished" : "started";
    emit({
      type: "subagent",
      name: raw.name ?? "subagent",
      status,
      description: raw.description ?? "",
    });
  }

  session.driver.on("agent-event", (event: AgentEvent) => {
    switch (event.type) {
      case "message_update":
        textBuffer = handleMessageUpdate(event, textBuffer);
        break;
      case "tool_call":
        handleToolCall(event);
        break;
      case "error":
        handleError(event);
        break;
      case "agent_end":
      case "turn_end":
        textBuffer = handleTurnBoundary(event, textBuffer);
        break;
      case "session_info":
        break;
      default:
        handleSubagent(event);
        break;
    }
  });

  return {
    get state() {
      return state;
    },

    send(text: string) {
      if (state === "stopped") return;
      setState("active");
      textBuffer = "";
      session.driver.prompt(text).catch((err: Error) => {
        emit({ type: "error", message: err.message });
        setState("idle");
      });
    },

    stop() {
      setState("stopped");
      session.driver.kill();
      void session.dispose();
    },
  };
}

/**
 * Create a telemetry sink that pipes {@link TelemetryLogEntry} events to the log callback.
 *
 * Wraps {@link renderLog} so that only displayable entries (generation, span_end for
 * tool/turn, trace lifecycle) are forwarded. Entries that `renderLog` returns `null`
 * for are silently suppressed. The returned function is used in `startRepl` to drain
 * buffered telemetry and to wire up live forwarding during the session.
 *
 * @param onLog - Callback invoked with an ANSI-rendered log string.
 * @returns A function that accepts a `TelemetryLogEntry` and routes it to `onLog`.
 *
 * @docLink packages/tui/concepts#repl-session
 */
export function createTelemetrySink(
  onLog: (line: string) => void,
): (entry: TelemetryLogEntry) => void {
  return (entry) => {
    const text = renderLog(entry);
    if (text) onLog(text);
  };
}
